// @ts-nocheck
import { useRef, useState } from 'react';
import Papa from 'papaparse';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { formatCalendarDate } from '@/lib/dates';
import { HotelTeamPicker, PickerTeam } from './HotelTeamPicker';
import { invalidateRoomBlockQueries } from './hooks/useRoomBlocks';

export const ROOM_BLOCK_HEADERS = ['Date Put On-Hold', 'Release Date', 'Event Name', 'Property Name', 'Group Name', 'Event Start Date', '# Rooms Originally Held', '# Rooms Reserved', '# Room Nights Available', 'Contact Email Address', 'Contact First Name', 'Contact Last Name', 'Contact Phone', 'Block Code', 'Report Date'];

export function RoomBlockUpload({ eventId, teams }: { eventId: string; teams: PickerTeam[] }) {
  const input = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [links, setLinks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState('');
  const [written, setWritten] = useState<number | null>(null);

  async function request(rawRows, dryRun, linkMap = {}) {
    const { data, error } = await invokeEdgeFunction('rle-hotel-import-room-blocks', {
      body: { event_id: eventId, rows: rawRows, dry_run: dryRun, link_map: linkMap },
    });
    if (error) throw error;
    if (data?.ok === false) throw new Error(data.error?.message || data.message || 'Import failed');
    const payload = data?.data ?? data;
    if (!payload?.counts || (dryRun && !Array.isArray(payload.rows))) throw new Error('The report preview returned an unexpected response.');
    return payload;
  }

  function handleFile(file?: File) {
    if (!file || busy) return;
    setPreview(null); setLinks({}); setRows([]); setWritten(null); setFileName(file.name);
    Papa.parse(file, { header: true, skipEmptyLines: 'greedy',
      complete: async results => {
        const missing = ROOM_BLOCK_HEADERS.filter(header => !results.meta.fields?.includes(header));
        if (missing.length) { toast.error(`Missing required columns: ${missing.join(', ')}`); return; }
        if (results.errors.length) { toast.error(results.errors[0].message); return; }
        setBusy(true);
        try { const payload = await request(results.data, true); setRows(results.data); setPreview(payload); }
        catch (error) { toast.error(error.message || 'Unable to preview report'); }
        finally { setBusy(false); }
      }, error: error => toast.error(error.message || 'Unable to read CSV'),
    });
  }

  async function handleImport() {
    if (!preview || busy) return;
    setBusy(true);
    try {
      const result = await request(rows, false, links);
      setWritten(Number(result.counts.written || 0));
      toast.success(`Imported ${result.counts.written || 0} room blocks.`);
      await invalidateRoomBlockQueries(qc);
    } catch (error) { toast.error(error.message || 'Unable to import report'); }
    finally { setBusy(false); }
  }

  return <div className="space-y-4">
    <div className="border-2 border-dashed rounded-md p-6 sm:p-10 text-center space-y-3"
      onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
      <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
      <p className="text-sm break-all">{fileName || 'Drop a Rooms On Hold CSV here'}</p>
      <Button variant="outline" className="min-h-11" disabled={busy} onClick={() => input.current?.click()}>{busy ? 'Processing…' : 'Choose CSV'}</Button>
      <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
    <p className="text-sm text-muted-foreground">Re-uploading the same report updates existing blocks by Block Code.</p>
    {preview && <>
      <div className="flex flex-wrap gap-2">{Object.entries(preview.counts).map(([key, count]) => <Badge variant="secondary" key={key}>{key.replace(/_/g, ' ')}: {String(count)}</Badge>)}</div>
      <div className="overflow-x-auto border rounded-md"><Table><TableHeader><TableRow>
        {['Block Code', 'Group', 'Hotel', 'Contact email', 'Reserved/Held', 'Release date', 'Status', 'Team'].map(label => <TableHead key={label}>{label}</TableHead>)}
      </TableRow></TableHeader><TableBody>
        {preview.rows.map((row, i) => {
          const code = row.block_code ?? row['Block Code'];
          const selected = links[code];
          const registrationId = selected || row.registration_id;
          const linked = !!registrationId;
          const hotelMissing = !row.hotel_property_id;
          const teamName = teams.find(t => t.registration_id === registrationId)?.team_name || row.team_name;
          return <TableRow key={`${code}-${i}`}>
            <TableCell>{code}</TableCell><TableCell>{row.group_name ?? row['Group Name']}</TableCell>
            <TableCell>{row.hotel_name ?? row['Property Name']}</TableCell><TableCell>{row.contact_email ?? row['Contact Email Address']}</TableCell>
            <TableCell className="whitespace-nowrap">{row.rooms_reserved ?? row['# Rooms Reserved']} / {row.rooms_held ?? row['# Rooms Originally Held']}</TableCell>
            <TableCell className="whitespace-nowrap">{formatCalendarDate(row.release_date ?? row['Release Date'], 'MMM d, yyyy', '—')}</TableCell>
            <TableCell><div className="space-y-1">
              <Badge variant="outline" className={linked ? 'bg-success/10 text-success border-success/30' : 'bg-warning/10 text-warning border-warning/30'}>{linked ? `Linked${teamName ? ` · ${teamName}` : ''}` : 'Unlinked'}</Badge>
              {!linked && <p className="text-xs text-warning">No registered coach with this email</p>}
              {hotelMissing && <p className="text-xs text-muted-foreground">Hotel not on event</p>}
            </div></TableCell>
            <TableCell>{!row.registration_id && <HotelTeamPicker teams={teams} value={selected} disabled={busy} onChange={id => setLinks(prev => ({ ...prev, [code]: id }))} />}</TableCell>
          </TableRow>;
        })}
      </TableBody></Table></div>
      <div className="flex flex-wrap items-center gap-3"><Button className="min-h-11" onClick={handleImport} disabled={busy || !rows.length}>{busy ? 'Importing…' : 'Import'}</Button>
        {written !== null && <p className="text-sm text-muted-foreground">{written} blocks written</p>}</div>
    </>}
  </div>;
}